// src/components/Miniroom/MiniroomView.jsx
import { useEffect, useState, useMemo } from "react";
import api from "../../api/axios";

const GRID = 32; // 서버 좌표 1칸 = 32px

export default function MiniroomView() {
    const [room, setRoom] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const { data } = await api.get("/api/miniroom/room/");
                if (mounted) setRoom(data);
            } catch (e) {
                console.error(e);
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, []);

    const sortedPlacements = useMemo(() => {
        if (!room?.placements) return [];
        // z_hint(우선) → y → x 정렬 
        return [...room.placements].sort((a, b) =>
            (a.z_hint || 0) - (b.z_hint || 0) || (a.y - b.y) || (a.x - b.x)
        );
    }, [room]);

    if (loading) return <div>로딩중…</div>;
    if (!room) return <div>방 정보를 불러오지 못했어요.</div>;

    const widthPx = room.width * GRID;
    const heightPx = room.height * GRID;

    // 배경 패턴: 서버에서 준 문자열을 그대로 backgroundImage에 사용
    const wallURL = room.wall_pattern || "";
    const floorURL = room.floor_pattern || "";

    return (
        <div style={{ display: "inline-block" }}>
            {/* 컨테이너 */}
            <div
                style={{
                    position: "relative",
                    width: widthPx,
                    height: heightPx,
                    overflow: "hidden",
                    imageRendering: "pixelated",
                    borderRadius: 8,
                    border: "1px solid rgba(0,0,0,0.1)",
                    backgroundColor: "#eaeaea",
                }}
            >
                {/* 벽 (상단 60% 영역 예시) */}
                <div
                    style={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        width: "100%",
                        height: heightPx * 0.6,
                        backgroundImage: wallURL ? `url(${wallURL})` : undefined,
                        backgroundRepeat: "repeat",
                        backgroundSize: `${GRID}px ${GRID}px`,
                        pointerEvents: "none",
                    }}
                />

                {/* 바닥 (하단 40% 영역 예시) */}
                <div
                    style={{
                        position: "absolute",
                        left: 0,
                        top: heightPx * 0.6,
                        width: "100%",
                        height: heightPx * 0.4,
                        backgroundImage: floorURL ? `url(${floorURL})` : undefined,
                        backgroundRepeat: "repeat",
                        backgroundSize: `${GRID}px ${GRID}px`,
                        pointerEvents: "none",
                    }}
                />

                {/* 가구들 (에디터 규칙과 동일) */}
                {sortedPlacements.map((p) => {
                    const fw = p?.furniture?.size_w ?? 1;
                    const fh = p?.furniture?.size_h ?? 1;
                    const w = (p.width || fw) * GRID;
                    const h = (p.height || fh) * GRID;
                    const imgSrc =
                        p.direction === "back" && p.furniture?.back_sprite_url
                            ? p.furniture.back_sprite_url
                            : p.furniture?.sprite_url;

                    // z-index: z_hint 우선 + y(아래일수록 위로) 
                    const z = (p.z_hint || 0) * 1000 + p.y + p.x / 1000;

                    return (
                        <div
                            key={p.id ?? `${p.furniture?.id}-${p.x}-${p.y}`}
                            style={{
                                position: "absolute",
                                left: p.x * GRID,
                                top: p.y * GRID,
                                width: w,
                                height: h,
                                zIndex: z,
                                pointerEvents: "none",
                            }}
                        >
                            <img
                                src={imgSrc}
                                alt={p.furniture?.name}
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    imageRendering: "pixelated",
                                    transform: `${p.mirror ? "scaleX(-1) " : ""}rotate(${p.rotation || 0}deg)`,
                                    transformOrigin: "center center",
                                    userSelect: "none",
                                    pointerEvents: "none",
                                    display: "block",
                                }}
                                draggable={false}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
